/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { EnduranceRbacUsersEnums } from '../enums/api/endurance-rbac-users.enums';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';
import { UsersEnum } from '../enums/api';

@Injectable()
export class UsersApiService {

    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get user details via the API
     * @param options
     */
    public getInfo(options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(EnduranceRbacUsersEnums.GET_USERS_ALL, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        // TODO: Create user object
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete a user by username via the API
     * @param id
     * @param options
     */
    public delete(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.delete(EnduranceRbacUsersEnums.DELETE_USER_ID(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Disable a user by username via the API
     * @param userObject
     * @param options
     */
    public disable(userObject: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(UsersEnum.DISABLE_USER, userObject, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Disable a user by username via the API
     * @param userObject
     * @param options
     */
    public enable(userObject: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(UsersEnum.ENABLE_USER, userObject, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Edit a user by username via the API
     * @param userObject
     * @param options
     */
    public edit(userObject: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(UsersEnum.PUT_USERS_EDIT, userObject, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Create a user via the API
     * @param userObject
     * @param options
     */
    public new(userObject: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.post(EnduranceRbacUsersEnums.POST_USERS_ADD, userObject, moduleAuth)
                .subscribe({
                    next: (data: any) => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }
}
